// Форматирование для экранов. Одно место на весь кабинет: даты и подписи
// стадий, разъехавшиеся по экранам, — верный способ показать одному лиду
// две разные стадии на соседних вкладках.

import type { Lead, LeadSummary, Stage } from "./types";

/** Лестница воронки по порядку. Отказ сюда НЕ входит: это исход, а не
 *  ступень, и рисовать его в ряду с остальными значило бы сказать, что
 *  к нему ведут. */
export const FUNNEL_ORDER: Stage[] = [
  "first_contact",
  "interest",
  "nurture",
  "ready",
  "client",
];

/** Стадии: подпись для человека и slug для атрибута data-stage,
 *  на котором держится цвет ступени в components.css. */
export const STAGE_LABEL: Record<Stage, { text: string; slug: string }> = {
  first_contact: { text: "Первый контакт", slug: "first-contact" },
  interest: { text: "Интерес", slug: "interest" },
  // Подписи — по словарю редизайна (docs/design/redesign-2026-10/mockups/
  // final/GLOSSARY.md): «Прогрев» → «Сравнивает», «Готов к сделке» →
  // «Готов купить», «Сделка» → «Клиент». Машинные значения стадий в базе
  // не менялись, меняется только текст экрана.
  nurture: { text: "Сравнивает", slug: "nurture" },
  ready: { text: "Готов купить", slug: "ready" },
  client: { text: "Клиент", slug: "client" },
  rejected: { text: "Отказ", slug: "rejected" },
};

/** Подписи каналов — одни на весь кабинет. Раньше словарь лежал в трёх
 *  экранах тремя копиями («Обзор», «Лиды», «Диалоги»), и подпись у лида
 *  и подпись на пилюле отбора могли бы разойтись ровно тогда, когда
 *  появится третий канал. Машинные значения — из контракта адаптеров
 *  (ARCHITECTURE §2): `telegram | site | vk`. */
export const CHANNEL_LABEL: Record<string, string> = {
  telegram: "Telegram",
  site: "Сайт",
  vk: "ВКонтакте",
};

export function channelLabel(channel: string): string {
  return CHANNEL_LABEL[channel] ?? channel;
}

/** Каналы, где у аккаунта есть комментарии под публикациями и, значит,
 *  модератор (ARCHITECTURE §2). Только у них на «Каналах» есть выбор
 *  «отвечать / только помечать»: у личной переписки выбирать нечего, и
 *  переключатель там был бы органом, который ничего не меняет. */
export const CHANNELS_WITH_COMMENTS: readonly string[] = ["vk"];

export function channelHasComments(channel: string): boolean {
  return CHANNELS_WITH_COMMENTS.includes(channel);
}

/** Пометка ветки владельцу словами — одни на строку списка, плашку диалога
 *  и отбор «Пометка». Машинные значения — `moderation.REVIEW_KINDS`. */
export const REVIEW_LABEL: Record<"negative" | "lead" | "question" | "uncertain", string> = {
  negative: "недовольный комментарий",
  lead: "возможный клиент",
  question: "вопрос",
  uncertain: "агент не уверен",
};

/** Каналы, по которым отбирают «Лиды» и «Диалоги». Только те, откуда лиды
 *  приходят сейчас: пилюля канала без единого лида за ней — орган, который
 *  выглядит рабочим и ничего не делает. Подписи — из словаря выше. */
export const CHANNEL_FILTERS: string[] = ["telegram", "site"];

/** Статусы задач дожима приходят из базы по-английски — это машинные
 *  значения из CHECK-ограничения, а не текст для человека. Перевод здесь,
 *  а не в базе: менять значения в БД ради вида в кабинете — способ однажды
 *  сломать планировщик. */
export const TASK_STATUS: Record<string, string> = {
  pending: "запланировано",
  done: "отправлено",
  cancelled: "снято",
  failed: "не доставлено",
};

export function taskStatus(value: string): string {
  return TASK_STATUS[value] ?? value;
}

const DAY = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });
const TIME = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const DATE = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" });
const FULL = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

/** Сегодняшнее показывается временем, вчерашнее — словом, остальное датой.
 *  «17 авг, 14:02» в списке из двадцати строк читается хуже, чем «14:02»:
 *  человеку важно, что это было сегодня, а не какое сегодня число. */
export function formatWhen(
  value: string | null,
  mode: "short" | "full" | "time" = "short",
): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  if (mode === "full") return FULL.format(date);
  if (mode === "time") return TIME.format(date);

  const now = new Date();
  const sameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (sameDay) return TIME.format(date);

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isYesterday) return "вчера";

  return DATE.format(date);
}

/** Как назвать лида. Имени может не быть — в Telegram человек вправе его
 *  не показывать, — тогда ник.
 *
 *  ⚠️ ВНЕШНИЙ ИДЕНТИФИКАТОР ПОКАЗЫВАЕМ НЕ ВСЕГДА. В Telegram это читаемый
 *  числовой id, и в крайнем случае он лучше пустоты. На сайте это
 *  идентификатор вкладки — сорок с лишним случайных знаков, и в списке
 *  лидов он занимает две строки, ничего не сообщая: у посетителя сайта
 *  имени нет и взяться ему неоткуда, пока он не оставит контакт.
 *
 *  Найдено живым прогоном виджета: первые же лиды с сайта встали в таблицу
 *  строками вида `kuoLD42UublY8yoORlePnNQdGHIHlueug2E-2vd6ldw`. */
export function leadTitle(lead: LeadSummary | Lead): string {
  if (lead.displayName) return lead.displayName;
  if ("username" in lead && lead.username) return lead.username;
  // Телефон, если человек его оставил: он и есть то, как владелец будет
  // о нём думать. Только в карточке — в списке контактов не показываем.
  if ("contactPhone" in lead && lead.contactPhone) return lead.contactPhone;
  if (lead.channel === "site") return "Посетитель сайта";
  return lead.externalId;
}


/** День для разделителя в ленте: «15 августа». */
export function formatDay(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : DAY.format(date);
}

/** Копейки → рубли. Деньги приходят целым числом копеек (ARCHITECTURE §7):
 *  в плавающей точке 166.48 превращается в 166.47999999999999.
 *
 *  ⚠️ ЭТО ЕДИНСТВЕННЫЙ СПОСОБ ПОКАЗАТЬ СУММУ С КОПЕЙКАМИ. Сервер кладёт
 *  в ответ ГОТОВУЮ строку `rubles` — «50.02», с точкой, потому что это
 *  машинное представление числа, а не текст для человека. Экраны местами
 *  выводили её напрямую, и в одном кабинете рядом стояли «50.02 ₽»
 *  и «40,55 ₽»: в русском интерфейсе дробная часть отделяется ЗАПЯТОЙ,
 *  а точка читается как разделитель тысяч.
 *
 *  Считать надо от `kopecks`, а строку `rubles` не показывать никогда. */
export function formatMoney(kopecks: number): string {
  return `${(kopecks / 100).toFixed(2).replace(".", ",")}\u00a0₽`;
}

/** Сумма для ПОЛЯ ВВОДА: те же рубли и копейки, что у `formatMoney`,
 *  но без знака «₽» — знак стоит в подписи поля, а не в значении.
 *
 *  Разделитель тот же, запятая. Поле правится руками, и точка в нём
 *  означала бы, что и вводить надо с точкой, — тогда как весь остальной
 *  кабинет показывает суммы с запятой. Разбор (`toKopecks` в Site.tsx)
 *  принимает оба знака, так что поле от этого ничего не теряет.
 */
export function rublesInput(kopecks: number): string {
  return (kopecks / 100).toFixed(2).replace(".", ",");
}

/** Целые рубли, как в макете: «1 240 ₽», а не «1 240,00 ₽».
 *
 *  Отдельно от formatMoney, а не вместо неё. В карточке лида и в отчёте копейки
 *  нужны — там сходят счёт с провайдером. В сводке за ночь они только шумят:
 *  владелец смотрит порядок суммы, а не второй знак после запятой. Округление
 *  ТОЛЬКО для показа: складываются по-прежнему целые копейки (ARCHITECTURE §7).
 */
const RUBLES = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });

// Intl для русской локали ставит разделителем узкий неразрывный пробел
// (U+202F). Приводим к обычному неразрывному (U+00A0): ширина как у пробела
// в макете, а «1 240 ₽» не разрывается переносом строки (канон, п. 17).
const NARROW_SPACES = /[\u202f\u2009]/g;

export function formatRubles(kopecks: number): string {
  const digits = RUBLES.format(Math.round(kopecks / 100)).replace(NARROW_SPACES, "\u00a0");
  return `${digits}\u00a0₽`;
}

/** Русское склонение после числа: 1 день, 2 дня, 5 дней. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = n % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
